import { faEyeSlash, faEye } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import { Col, Form } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { loginUser } from 'Api/userapi';
import { toast } from 'react-hot-toast';
import { Formik, Form as FormikForm, Field, ErrorMessage } from 'formik';
import { loginValidationSchema } from 'utils/validationSchemas';
import { useState } from 'react';
import './style.css';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';

import { googleauthUser } from 'Api/GoogleApi';

interface Credentials {
  email: string;
  password: string;
}

const SignInForm = ({ layout }: { layout: 'simple' | 'card' | 'split' }) => {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (
    values: Credentials,
    { resetForm }: { resetForm: () => void }
  ) => {
    try {
      const trimmedValues = {
        email: values.email.trim(),
        password: values.password.trim()
      };
      const response = await loginUser(trimmedValues);
      if (response.status === 200) {
        navigate('/dashboard/properties');
        resetForm();
        toast.success(` ${response.data.message}`);
      } else {
        toast.error('Failed to log in. Please try again.');
      }
    } catch (error: any) {
      console.error('Error during login:', error);
      if (error.response) {
        toast.error(`${error.response.data.message}`);
      } else {
        toast.error(
          'An error occurred during login. Please check your connection and try again.'
        );
      }
    }
  };

  const handleLoginSuccess = async (credentialResponse: CredentialResponse) => {
    if (!credentialResponse.credential) {
      console.error('Google Login failed: No credential received');
      toast.error('Google login failed');
      return;
    }

    try {
      const payload = { token: credentialResponse.credential };
      const res = await googleauthUser(payload);

      if (res.status === 200) {
        toast.success(res.data.message);
        navigate('/dashboard/properties');
      } else {
        toast.error('Google login failed');
      }
    } catch (error) {
      console.error('Error during Google login:', error);
      toast.error('An error occurred during Google login');
    }
  };

  return (
    <>
      <div>
        <div className="text-center mb-4">
          <h3 className="text-body-highlight">Sign in</h3>
          <p className="text-body-tertiary">Get access to your account</p>
        </div>

        <Formik
          initialValues={{ email: '', password: '' }}
          validationSchema={loginValidationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, isSubmitting }) => (
            <FormikForm>
              {/* Email Field */}
              <Form.Group className="mb-3 text-start">
                <div className="fs-9">Email address</div>
                <Field
                  name="email"
                  type="email"
                  className={`form-control form-icon-input ${
                    errors.email && touched.email ? 'is-invalid' : ''
                  }`}
                  placeholder="name@example.com"
                />

                <ErrorMessage
                  name="email"
                  component="div"
                  className="text-danger mt-1 fs-9"
                />
              </Form.Group>
              <div className="mb-3 position-relative">
                <label className="fs-9">Password</label>
                <Field
                  name="password"
                  type={passwordVisible ? 'text' : 'password'}
                  className={`form-control ${
                    errors.password && touched.password ? 'is-invalid' : ''
                  }`}
                  placeholder="Password"
                />
                <FontAwesomeIcon
                  icon={passwordVisible ? faEye : faEyeSlash}
                  className="eye-icon"
                  onClick={() => setPasswordVisible(!passwordVisible)}
                />
                <ErrorMessage
                  name="password"
                  component="div"
                  className="text-danger mt-1 fs-9"
                />
              </div>

              <div className="text-start mb-4">
                <Col xs="auto" className="text-xs-small">
                  No account?&nbsp;
                  <Link
                    to={`/sign-up`}
                    className="fs-16 text-decoration-none text-xs-small"
                  >
                    Sign up now
                  </Link>
                  &nbsp;
                  <span>|</span>&nbsp;
                  <Link
                    to={'/forgot-password'}
                    className="text-decoration-none text-xs-small"
                  >
                    Forgot password?
                  </Link>
                </Col>
              </div>
              {/* Submit Button */}
              <div className="text-center  w-full">
                <Button
                  variant="primary"
                  className="mb-3 w-full"
                  type="submit"
                  disabled={isSubmitting}
                >
                  Login
                </Button>
              </div>

              <div className="position-relative">
                <hr className="bg-body-secondary mt-2 mb-4" />
                <div className="divider-content-center bg-body-emphasis">
                  Or
                </div>
              </div>
              <div className="text-center">
                <div style={{ width: '250px', margin: '0 auto' }}>
                  <GoogleLogin
                    onSuccess={handleLoginSuccess}
                    onError={() => {
                      toast.error('Google login failed');
                    }}
                  />
                </div>
              </div>
            </FormikForm>
          )}
        </Formik>
      </div>
    </>
  );
};

export default SignInForm;
